import type { CSSProperties } from 'react';

type StyleMap = Record<string, CSSProperties>;

export const s: StyleMap = {
  // ── Layout ────────────────────────────────────────────────────────────────
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.35)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: 16,
  },
  modal: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 20,
    width: '100%',
    maxWidth: 640,
    maxHeight: '93vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(15,23,42,0.12)',
    animation: 'fadeUp 0.25s ease',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '22px 24px 14px',
  },
  body: {
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
    background: '#f8fafc',
  },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 },
  row3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 9 },

  // ── Header ────────────────────────────────────────────────────────────────
  badge: {
    display: 'inline-block',
    background: 'linear-gradient(90deg,#eff6ff,#f5f3ff)',
    color: '#4f46e5',
    border: '1px solid #c7d2fe',
    borderRadius: 20,
    padding: '3px 12px',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    marginBottom: 6,
  },
  title: {
    margin: 0,
    fontSize: 21,
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-0.02em',
  },
  closeBtn: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    color: '#64748b',
    borderRadius: 8,
    width: 30,
    height: 30,
    cursor: 'pointer',
    fontSize: 12,
    flexShrink: 0,
  },

  // ── Cards ─────────────────────────────────────────────────────────────────
  card: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    padding: 16,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  cardTitle: { fontSize: 12, fontWeight: 700, color: '#1e293b' },

  // ── Form ──────────────────────────────────────────────────────────────────
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 10, color: '#64748b', fontWeight: 600 },
  input: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '8px 11px',
    color: '#0f172a',
    fontSize: 12,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputSm: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 7,
    padding: '6px 9px',
    color: '#0f172a',
    fontSize: 11,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  textarea: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 9,
    padding: '10px 12px',
    color: '#0f172a',
    fontSize: 12,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    lineHeight: 1.6,
  },

  // ── Buttons ───────────────────────────────────────────────────────────────
  triggerBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '5px 12px',
    cursor: 'pointer',
    boxShadow: '0 4px 20px #2563eb30',
  },
  primaryBtn: {
    background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
    color: '#fff',
    border: 'none',
    borderRadius: 11,
    padding: '13px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 16px #2563eb25',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
  },
  approveBtn: {
    background: 'linear-gradient(135deg,#16a34a,#15803d)',
    color: '#fff',
    border: 'none',
    borderRadius: 11,
    padding: '13px',
    fontSize: 13,
    fontWeight: 700,
    boxShadow: '0 4px 16px #16a34a25',
    display: 'block',
    boxSizing: 'border-box',
  },
  backBtn: {
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    color: '#475569',
    borderRadius: 11,
    padding: '13px 18px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  ghostBtn: {
    fontSize: 10,
    color: '#7c3aed',
    background: '#f5f3ff',
    border: '1px solid #ddd6fe',
    borderRadius: 6,
    padding: '4px 10px',
    cursor: 'pointer',
    fontWeight: 600,
    flexShrink: 0,
  },
  btnRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
  },
  spinner: {
    display: 'inline-block',
    width: 13,
    height: 13,
    border: '2px solid #ffffff60',
    borderTop: '2px solid #fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },

  // ── Feedback ──────────────────────────────────────────────────────────────
  sectionLabel: {
    fontSize: 10,
    color: '#94a3b8',
    fontWeight: 700,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
  },
  exampleChip: {
    fontSize: 10,
    color: '#2563eb',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 20,
    padding: '3px 10px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  inlineError: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '8px 12px',
    color: '#dc2626',
    fontSize: 11,
  },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 9,
    padding: '9px 13px',
    color: '#dc2626',
    fontSize: 11,
  },
  notesBanner: {
    display: 'flex',
    gap: 9,
    alignItems: 'flex-start',
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
    borderRadius: 10,
    padding: '10px 13px',
  },

  // ── Ply configurator ──────────────────────────────────────────────────────
  plyBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 20,
    background: '#f5f3ff',
    color: '#7c3aed',
    border: '1px solid #ddd6fe',
  },
  plyCtrlBtn: {
    fontSize: 11,
    fontWeight: 600,
    padding: '5px 11px',
    borderRadius: 7,
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    color: '#475569',
    cursor: 'pointer',
  },
  plyDiagram: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginBottom: 12,
  },
  plySlot: { border: '1px solid', borderRadius: 10, padding: '10px 12px' },
  plyLayerLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  plyTypeTag: {
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 9px',
    borderRadius: 20,
  },
  plyFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill,minmax(110px,1fr))',
    gap: 8,
  },
  readonlyPill: {
    background: '#fffbeb',
    border: '1px solid #fde68a',
    color: '#d97706',
    borderRadius: 7,
    padding: '6px 9px',
    fontSize: 11,
    fontWeight: 700,
    textAlign: 'center',
  },
  formulaBox: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 9,
    padding: '10px 13px',
  },

  // ── Results ───────────────────────────────────────────────────────────────
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '12px 16px',
    flexWrap: 'wrap',
  },
  summaryItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  summaryDot: {
    width: 4,
    height: 4,
    borderRadius: '50%',
    background: '#cbd5e1',
    flexShrink: 0,
  },
  assumptionBox: {
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: 10,
    padding: '10px 13px',
  },
  breakdownToggle: {
    background: 'transparent',
    border: '1px solid #e2e8f0',
    color: '#64748b',
    borderRadius: 7,
    padding: '4px 11px',
    fontSize: 10,
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  breakdownTable: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 9,
    padding: '10px 12px',
  },
  vendorCard: {
    border: '1px solid',
    borderRadius: 12,
    padding: '14px 16px',
    transition: 'border-color 0.15s, background 0.15s',
  },
  bestBadge: {
    fontSize: 10,
    fontWeight: 700,
    color: '#2563eb',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 20,
    padding: '3px 10px',
    whiteSpace: 'nowrap',
  },
  recommendBox: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 10,
    padding: '11px 14px',
  },
  tipBox: {
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 9,
    padding: '9px 13px',
    color: '#92400e',
    fontSize: 11,
    lineHeight: 1.6,
    display: 'flex',
    gap: 7,
  },
};

export const globalCSS = `
  @keyframes spin   { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
  input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
  input:focus, select:focus, textarea:focus { outline: 2px solid #2563eb40; border-color: #2563eb !important; }
  select option { background: #ffffff; color: #1e293b; }
  textarea { resize: vertical; }
  ::-webkit-scrollbar       { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
`;
